/* src/modules/performance/MyPerformance.js */
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, getDocs, orderBy, Timestamp, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
    CheckCircleIcon, 
    ExclamationIcon, 
    FileTextIcon, 
    PlusIcon, 
    TrashIcon, 
    SaveIcon,
    SendIcon,
    EyeIcon,
    LockClosedIcon,
    PencilIcon,
    DownloadIcon
} from '../../components/icons/Icons';

// --- CONSTANTS ---
const COMPANY_VALUES = [
    "Commitment",
    "Collaboration",
    "Customer-First"
];

const RATING_OPTIONS = [
    "Greatly Exceeds Expectations",
    "Exceeds Expectations",
    "Meets Expectations",
    "Needs Improvement",
    "Needs Significant Improvement"
];

// --- HELPER: ISO Date Formatter ---
const formatDateISO = (val) => {
    if (!val) return '-';
    try {
        const date = val.toDate ? val.toDate() : new Date(val);
        return date.toISOString().split('T')[0];
    } catch (e) {
        return val;
    }
};

export default function MyPerformance({ userProfile, appId }) {
    const [view, setView] = useState('list'); // 'list' or 'form'
    const [dashboardRows, setDashboardRows] = useState([]);
    const [selectedReview, setSelectedReview] = useState(null);
    const [selectedTemplateForForm, setSelectedTemplateForForm] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const init = async () => {
            try {
                // 1. Fetch ALL Reviews for this user
                const reviewsRef = collection(db, `artifacts/${appId}/performance_reviews`);
                const rQuery = query(reviewsRef, where('userId', '==', userProfile.uid));
                const reviewsSnap = await getDocs(rQuery);
                const userReviews = reviewsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // 2. Fetch ALL Templates (to match deadlines)
                const templatesRef = collection(db, `artifacts/${appId}/performance_templates`);
                const tQuery = query(templatesRef, orderBy('createdAt', 'desc')); 
                const templatesSnap = await getDocs(tQuery);
                const allTemplates = templatesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // 3. Merge Logic
                const rows = [];

                // A. Existing reviews
                userReviews.forEach(review => {
                    const tmpl = allTemplates.find(t => t.id === review.templateId) || {};
                    rows.push({
                        type: 'review',
                        key: review.id,
                        id: review.id,
                        cycleName: review.cycle,
                        status: review.status,
                        dueDate: tmpl.deadline || 'N/A',
                        lastEdited: review.updatedAt,
                        finalRating: review.finalRating,
                        data: review,
                        templateData: tmpl
                    });
                });

                // B. Not Started Active Templates
                allTemplates.forEach(tmpl => {
                    const hasReview = userReviews.some(r => r.templateId === tmpl.id);
                    if (!hasReview) {
                        rows.push({
                            type: 'template',
                            key: tmpl.id,
                            id: tmpl.id,
                            cycleName: tmpl.name,
                            // CHANGE: Show as 'Closed' if the cycle was closed before they started
                            status: tmpl.status === 'Closed' ? 'Closed' : 'Not Started',
                            dueDate: tmpl.deadline,
                            lastEdited: null,
                            finalRating: '-',
                            data: null,
                            templateData: tmpl
                        });
                    }
                });

                // Sort by Due Date (descending)
                rows.sort((a, b) => {
                    const dateA = new Date(a.dueDate);
                    const dateB = new Date(b.dueDate);
                    return dateB - dateA;
                });

                setDashboardRows(rows);
                setIsLoading(false);
            } catch (err) {
                console.error("Error loading dashboard:", err);
                setIsLoading(false);
            }
        };

        init();
    }, [appId, userProfile.uid, view]);

    // --- HANDLERS ---

    const handleStart = async (template) => {
        setIsLoading(true);
        try {
            const newReviewData = {
                userId: userProfile.uid,
                userName: userProfile.name,
                managerId: userProfile.managerId || '',
                templateId: template.id,
                cycle: template.name,
                status: 'Draft',
                employeeResponses: {},
                managerComments: {},
                finalRating: '',
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            };

            const docRef = await addDoc(collection(db, `artifacts/${appId}/performance_reviews`), newReviewData);
            
            setSelectedReview({ id: docRef.id, ...newReviewData });
            setSelectedTemplateForForm(template);
            setView('form');
        } catch (error) {
            console.error(error);
            alert("Failed to start review.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditOrView = (reviewRow) => {
        setSelectedReview(reviewRow.data);
        setSelectedTemplateForForm(reviewRow.templateData); 
        setView('form');
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div>
            {view === 'list' && (
                <div className="space-y-6">
                    <div className="flex justify-between items-center bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <div>
                            <h2 className="text-xl font-bold text-[#213368]">My Performance Reviews</h2>
                            <p className="text-sm text-gray-500">Manage your self-evaluations and view assessment history.</p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cycle Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Edited</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {dashboardRows.map(row => (
                                    <tr key={row.key} className="hover:bg-gray-50 transition">
                                        {/* COLUMN 1: Cycle Name */}
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-[#213368]">
                                            {row.cycleName}
                                        </td>
                                        
                                        {/* COLUMN 2: Status Badge */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <StatusBadge status={row.status} />
                                        </td>
                                        
                                        {/* COLUMN 3: Due Date */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                                            {row.dueDate}
                                        </td>
                                        
                                        {/* COLUMN 4: Last Edited */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                            {formatDateISO(row.lastEdited)}
                                        </td>
                                        
                                        {/* COLUMN 5: Action Buttons */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {row.status === 'Not Started' ? (
                                                <button 
                                                    onClick={() => handleStart(row.templateData)}
                                                    className="flex items-center px-3 py-1.5 bg-[#20BEC6] text-white text-xs font-bold rounded hover:opacity-90 shadow-sm"
                                                >
                                                    <PlusIcon className="h-3 w-3 mr-1" /> Start
                                                </button>
                                            ) : row.status === 'Closed' ? (
                                                <span className="text-gray-400 text-sm italic">Missed Deadline</span>
                                            ) : row.status === 'Draft' ? (
                                                <button 
                                                    onClick={() => handleEditOrView(row)}
                                                    className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                >
                                                    <PencilIcon className="h-4 w-4 mr-1" /> Edit
                                                </button>
                                            ) : (
                                                <div className="flex items-center text-gray-500">
                                                    {row.status === 'Completed' && <LockClosedIcon className="h-4 w-4 mr-2 text-green-600" />}
                                                    {row.status === 'Review Closed' && <LockClosedIcon className="h-4 w-4 mr-2 text-gray-500" />}
                                                    <button 
                                                        onClick={() => handleEditOrView(row)}
                                                        className="hover:text-[#213368] text-sm flex items-center"
                                                    >
                                                        <EyeIcon className="h-4 w-4 mr-1" /> View
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {dashboardRows.length === 0 && (
                                    <tr><td colSpan="5" className="text-center py-8 text-gray-500">No review cycles found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {view === 'form' && selectedReview && selectedTemplateForForm && (
                <ReviewForm 
                    appId={appId}
                    review={selectedReview}
                    template={selectedTemplateForForm}
                    onBack={() => setView('list')}
                />
            )}
        </div>
    );
}

// --- HELPER: Status Badge ---
function StatusBadge({ status }) {
    const styles = {
        'Not Started': 'bg-gray-100 text-gray-600',
        'Draft': 'bg-yellow-100 text-yellow-800',
        'Submitted': 'bg-blue-100 text-blue-800',
        'Manager-Review': 'bg-purple-100 text-purple-800',
        'Review Complete': 'bg-teal-100 text-teal-800',
        'Completed': 'bg-green-100 text-green-800',
        'Review Closed': 'bg-gray-200 text-gray-700',
        'Closed': 'bg-gray-200 text-gray-700'
    };
    return (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || 'bg-gray-100'}`}>
            {status}
        </span>
    );
}

// --- SUB-COMPONENT: The Form ---
function ReviewForm({ appId, review, template, onBack }) {
    const [responses, setResponses] = useState(review.employeeResponses || {});
    const [isSaving, setIsSaving] = useState(false);
    
    // Employee can only edit if status is Draft
    const isReadOnly = review.status !== 'Draft';

    // --- NEW: Visibility Gate ---
    // Only show feedback if sent back (Draft) or finalized
    const showManagerFeedback = ['Draft', 'Review Complete', 'Completed', 'Review Closed'].includes(review.status);
    
    let feedbackPlaceholder = "No feedback provided yet.";
    if (!showManagerFeedback && ['Submitted', 'Manager-Review'].includes(review.status)) {
        feedbackPlaceholder = "Feedback will be visible once the manager completes the review.";
    }

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await updateDoc(doc(db, `artifacts/${appId}/performance_reviews`, review.id), {
                employeeResponses: responses,
                updatedAt: Timestamp.now()
            });
            alert("Draft saved.");
        } catch (error) {
            console.error(error);
            alert("Save failed.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = async () => {
        if (!window.confirm("Submit review? You won't be able to edit after this.")) return;
        setIsSaving(true);
        try {
            await updateDoc(doc(db, `artifacts/${appId}/performance_reviews`, review.id), {
                employeeResponses: responses,
                status: 'Submitted',
                // If this was a re-submission (had a rejectionReason), clear it now
                rejectionReason: null,
                submittedAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });
            onBack();
        } catch (error) {
            console.error(error);
            alert("Submit failed.");
        } finally {
            setIsSaving(false);
        }
    };

    const updateSectionData = (sectionId, data) => {
        setResponses(prev => ({ ...prev, [sectionId]: data }));
    };

    // --- NEW: PDF Download Handler for Employee View ---
    const handleDownloadPDF = () => {
        if (review.status !== 'Completed' && review.status !== 'Review Closed') {
            alert("PDF downloads are only available for Completed or Closed reviews.");
            return;
        }
        const doc = new jsPDF();
        
        const logo = new Image();
        logo.src = '/Brand_logo.png';
        
        logo.onload = () => {
            const imgProps = doc.getImageProperties(logo);
            const pdfLogoWidth = 40; 
            const pdfLogoHeight = (imgProps.height * pdfLogoWidth) / imgProps.width;

            const darkBlue = [33, 51, 104];
            const teal = [32, 190, 198];

            // Header
            doc.addImage(logo, 'PNG', 14, 10, pdfLogoWidth, pdfLogoHeight);
            doc.setFontSize(10);
            doc.setTextColor(200, 0, 0);
            doc.text("CONFIDENTIAL", 195, 15, { align: "right" });
            
            doc.setTextColor(...darkBlue);
            doc.setFontSize(16);
            doc.text("Performance Review", 14, pdfLogoHeight + 18);
            
            // Metadata
            doc.setTextColor(0);
            doc.setFontSize(10);
            const startY = pdfLogoHeight + 25;
            
            doc.text(`Employee: ${review.userName}`, 14, startY);
            doc.text(`Manager: ${review.managerName || 'Assigned Manager'}`, 14, startY + 5);
            doc.text(`Cycle: ${review.cycle}`, 14, startY + 10);
            doc.text(`Status: ${review.status}`, 14, startY + 15); 
            doc.text(`Date: ${formatDateISO(new Date())}`, 14, startY + 20);
            
            // Divider
            doc.setDrawColor(...teal);
            doc.setLineWidth(0.5);
            doc.line(14, startY + 25, 196, startY + 25);

            let finalY = startY + 33; 

            // Sections
            template.sections.forEach((section, index) => {
                if (finalY > 250) { doc.addPage(); finalY = 20; }

                doc.setTextColor(...darkBlue);
                doc.setFontSize(12);
                doc.setFont("helvetica", "bold");
                doc.text(`${index + 1}. ${section.title}`, 14, finalY);
                finalY += 8;

                doc.setTextColor(0);
                doc.setFontSize(10);
                doc.setFont("helvetica", "normal");

                const employeeResponse = responses[section.id] || []; 
                const mgrComment = review.managerComments?.[section.id] || "No comments.";

                if (section.type === 'text') {
                    const text = typeof employeeResponse === 'string' ? employeeResponse : "No response.";
                    const splitText = doc.splitTextToSize(text, 180);
                    doc.text(splitText, 14, finalY);
                    finalY += (splitText.length * 5) + 5;
                } 
                else if (section.type === 'achievements_table') {
                    const bodyData = Array.isArray(employeeResponse) ? employeeResponse.map(row => [row.text, row.value]) : [];
                    autoTable(doc, {
                        startY: finalY,
                        head: [['Accomplishment', 'Value']],
                        body: bodyData,
                        theme: 'grid',
                        headStyles: { fillColor: darkBlue },
                        margin: { left: 14, right: 14 }
                    });
                    finalY = doc.lastAutoTable.finalY + 5;
                }
                else if (section.type === 'standard_table') {
                    const bodyData = Array.isArray(employeeResponse) ? employeeResponse.map(row => [row.text]) : [];
                    autoTable(doc, {
                        startY: finalY,
                        head: [['Description']],
                        body: bodyData,
                        theme: 'grid',
                        headStyles: { fillColor: darkBlue },
                        margin: { left: 14, right: 14 }
                    });
                    finalY = doc.lastAutoTable.finalY + 5;
                }
                else if (section.type === 'training_table') {
                    const bodyData = Array.isArray(employeeResponse) ? employeeResponse.map(row => [row.category, row.name, row.status]) : [];
                    autoTable(doc, {
                        startY: finalY,
                        head: [['Category', 'Activity', 'Status']],
                        body: bodyData,
                        theme: 'grid',
                        headStyles: { fillColor: darkBlue },
                        margin: { left: 14, right: 14 }
                    });
                    finalY = doc.lastAutoTable.finalY + 5;
                }

                if (finalY > 250) { doc.addPage(); finalY = 20; }
                
                doc.setFont("helvetica", "bold");
                doc.setTextColor(100);
                doc.text("Manager Feedback:", 14, finalY);
                finalY += 5;
                
                doc.setFont("helvetica", "normal");
                doc.setTextColor(0);
                
                if (section.type === 'rating') {
                    doc.text(`Final Rating: ${review.finalRating || 'N/A'}`, 14, finalY);
                    finalY += 6;
                }

                const splitComment = doc.splitTextToSize(mgrComment, 180);
                doc.text(splitComment, 14, finalY);
                finalY += (splitComment.length * 5) + 15;
            });

            const safeName = review.userName.replace(/[^a-z0-9]/gi, '_');
            const safeCycle = review.cycle.replace(/[^a-z0-9]/gi, '_');
            doc.save(`${safeCycle}_${safeName}.pdf`);
        };
    };

    return (
        <div className="space-y-6">
            <button onClick={onBack} className="text-sm text-gray-500 hover:text-[#213368] flex items-center">
                ← Back to My Reviews
            </button>

            {/* REJECTION BANNER: Shows if manager sent back */}
            {!isReadOnly && review.rejectionReason && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r shadow-sm animate-pulse-once">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <ExclamationIcon className="h-5 w-5 text-red-500" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-bold text-red-800">Action Required: Review Returned by Manager</h3>
                            <p className="text-sm text-red-700 mt-1">
                                Your manager has requested changes. Please address the feedback below and resubmit.
                            </p>
                            <p className="text-sm font-medium text-red-800 mt-2 bg-red-100 p-2 rounded">
                                " {review.rejectionReason} "
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Status Header */}
            <div className={`p-4 rounded-lg flex flex-col md:flex-row md:items-center justify-between shadow-sm ${
                review.status === 'Completed' ? 'bg-green-50 border border-green-200' : 
                review.status === 'Submitted' || review.status === 'Manager-Review' || review.status === 'Review Complete' ? 'bg-blue-50 border border-blue-200' : 
                'bg-yellow-50 border border-yellow-200'
            }`}>
                <div className="flex items-center space-x-3 mb-2 md:mb-0">
                    {review.status === 'Completed' ? <LockClosedIcon className="text-green-600 h-6 w-6" /> : 
                     review.status === 'Draft' ? <PencilIcon className="text-yellow-600 h-6 w-6" /> :
                     <CheckCircleIcon className="text-blue-600 h-6 w-6" />}
                    <div>
                        <h2 className="text-lg font-bold text-[#213368]">{template.name}</h2>
                        <p className="text-sm text-gray-700 font-medium">
                            Status: {review.status} {review.status === 'Completed' && "(Locked)"}
                        </p>
                        {review.submittedAt && (
                            <p className="text-xs text-gray-500 font-mono mt-1">
                                Submitted: {formatDateISO(review.submittedAt)}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex space-x-3">
                    {!isReadOnly && (
                        <>
                            <button onClick={handleSave} disabled={isSaving} className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition">
                                <SaveIcon className="w-4 h-4 mr-2" /> Save Draft
                            </button>
                            <button onClick={handleSubmit} disabled={isSaving} className="flex items-center px-4 py-2 bg-[#20BEC6] text-white rounded-lg hover:opacity-90 shadow transition">
                                <SendIcon className="w-4 h-4 mr-2" /> Submit
                            </button>
                        </>
                    )}
                    {/* UPDATED: Download PDF Button logic */}
                    {(review.status === 'Completed' || review.status === 'Review Closed') && (
                        <button 
                            onClick={handleDownloadPDF} 
                            className="flex items-center px-4 py-2 bg-[#213368] text-white rounded-lg hover:opacity-90 shadow-sm transition"
                        >
                            <DownloadIcon className="w-4 h-4 mr-2" /> Download PDF
                        </button>
                    )}
                </div>
            </div>

            {/* Render Sections */}
            <div className="space-y-8">
                {template.sections.map((section, index) => {
                    const sectionData = responses[section.id];
                    
                    // --- UPDATED: Apply Visibility Gate to Comments ---
                    const managerComment = (showManagerFeedback && review.managerComments) 
                        ? review.managerComments[section.id] 
                        : null;

                    return (
                        <div key={section.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                            <div className="mb-4 border-b pb-2">
                                <h3 className="text-xl font-bold text-[#213368]">{index + 1}. {section.title}</h3>
                                {section.description && <p className="text-sm text-gray-500 mt-1 whitespace-pre-wrap">{section.description}</p>}
                            </div>

                            {/* --- CONTENT AREA (Grid layout to show manager comments side-by-side if text) --- */}
                            
                            {/* 1. TEXT TYPE */}
                            {section.type === 'text' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Your Response</label>
                                        <textarea
                                            disabled={isReadOnly}
                                            value={sectionData || ''}
                                            onChange={(e) => updateSectionData(section.id, e.target.value)}
                                            rows={6}
                                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#20BEC6] focus:outline-none"
                                            placeholder="Type here..."
                                        />
                                    </div>
                                    {/* Manager Comment Block */}
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1 flex items-center">
                                            Manager Feedback
                                            {managerComment && <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-[10px] rounded-full">New</span>}
                                        </label>
                                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{managerComment || <span className="text-gray-400 italic">{feedbackPlaceholder}</span>}</p>
                                    </div>
                                </div>
                            )}

                            {/* 2. TABLE TYPES (Achievements, Standard, Training) */}
                            {(section.type === 'achievements_table' || section.type === 'standard_table' || section.type === 'training_table') && (
                                <div>
                                    {/* Render the specific table */}
                                    {section.type === 'achievements_table' && <AchievementsTable data={sectionData || []} onChange={(newData) => updateSectionData(section.id, newData)} isReadOnly={isReadOnly} />}
                                    {section.type === 'standard_table' && <StandardTable data={sectionData || []} onChange={(newData) => updateSectionData(section.id, newData)} isReadOnly={isReadOnly} />}
                                    {section.type === 'training_table' && <TrainingTable data={sectionData || []} onChange={(newData) => updateSectionData(section.id, newData)} isReadOnly={isReadOnly} />}
                                    
                                    {/* Manager Comment Block (Below Table) */}
                                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Manager Feedback on this Section</label>
                                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{managerComment || <span className="text-gray-400 italic">{feedbackPlaceholder}</span>}</p>
                                    </div>
                                </div>
                            )}

                            {/* 5. RATING */}
                            {section.type === 'rating' && (
                                <div className="p-6 bg-blue-50 border border-blue-100 rounded-lg text-center">
                                    <h4 className="text-lg font-bold text-[#213368] mb-2">Overall Performance Rating</h4>
                                    
                                    {/* --- UPDATED: Gate the Final Rating --- */}
                                    {(showManagerFeedback && review.finalRating) ? (
                                        <div className="animate-fade-in">
                                            <p className="text-xl font-bold text-[#20BEC6] mb-2">{review.finalRating}</p>
                                            <p className="text-sm text-gray-600">Assessed by Manager</p>
                                            {managerComment && (
                                                <div className="mt-4 p-4 bg-white rounded border text-left">
                                                    <p className="text-xs font-bold text-gray-500 uppercase mb-1">Manager Comments</p>
                                                    <p className="text-gray-800">{managerComment}</p>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 italic">
                                            {showManagerFeedback 
                                                ? 'To be assessed by Manager upon submission.' 
                                                : 'Rating will be visible once the manager completes the review.'}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// --- TABLE COMPONENTS ---
function AchievementsTable({ data, onChange, isReadOnly }) {
    const addRow = () => onChange([...data, { id: Date.now(), text: '', value: '' }]);
    const updateRow = (id, field, val) => onChange(data.map(row => row.id === id ? { ...row, [field]: val } : row));
    const removeRow = (id) => onChange(data.filter(row => row.id !== id));

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase w-2/3">Accomplishment</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase w-1/3">Company Value</th>
                        {!isReadOnly && <th className="px-4 py-3 w-10"></th>}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((row) => (
                        <tr key={row.id}>
                            <td className="px-4 py-2"><textarea disabled={isReadOnly} className="w-full p-2 border rounded text-sm" rows="3" value={row.text} onChange={(e) => updateRow(row.id, 'text', e.target.value)} /></td>
                            <td className="px-4 py-2 align-top">
                                <select disabled={isReadOnly} className="w-full p-2 border rounded text-sm bg-white" value={row.value} onChange={(e) => updateRow(row.id, 'value', e.target.value)}>
                                    <option value="">Select...</option>
                                    {COMPANY_VALUES.map(v => <option key={v} value={v}>{v}</option>)}
                                </select>
                            </td>
                            {!isReadOnly && <td className="px-4 py-2 align-top text-center"><button onClick={() => removeRow(row.id)} className="text-red-400 hover:text-red-600"><TrashIcon className="h-5 w-5" /></button></td>}
                        </tr>
                    ))}
                </tbody>
            </table>
            {!isReadOnly && <button onClick={addRow} className="mt-2 text-sm text-[#20BEC6] font-medium hover:underline flex items-center"><PlusIcon className="h-4 w-4 mr-1" /> Add Row</button>}
        </div>
    );
}

function StandardTable({ data, onChange, isReadOnly }) {
    const addRow = () => onChange([...data, { id: Date.now(), text: '' }]);
    const updateRow = (id, val) => onChange(data.map(row => row.id === id ? { ...row, text: val } : row));
    const removeRow = (id) => onChange(data.filter(row => row.id !== id));

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase w-full">Description</th>
                        {!isReadOnly && <th className="px-4 py-3 w-10"></th>}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((row) => (
                        <tr key={row.id}>
                            <td className="px-4 py-2"><textarea disabled={isReadOnly} className="w-full p-2 border rounded text-sm" rows="2" value={row.text} onChange={(e) => updateRow(row.id, e.target.value)} /></td>
                            {!isReadOnly && <td className="px-4 py-2 align-top text-center"><button onClick={() => removeRow(row.id)} className="text-red-400 hover:text-red-600"><TrashIcon className="h-5 w-5" /></button></td>}
                        </tr>
                    ))}
                </tbody>
            </table>
            {!isReadOnly && <button onClick={addRow} className="mt-2 text-sm text-[#20BEC6] font-medium hover:underline flex items-center"><PlusIcon className="h-4 w-4 mr-1" /> Add Row</button>}
        </div>
    );
}

function TrainingTable({ data, onChange, isReadOnly }) {
    const addRow = () => onChange([...data, { id: Date.now(), category: '', name: '', status: '' }]);
    const updateRow = (id, field, val) => onChange(data.map(row => row.id === id ? { ...row, [field]: val } : row));
    const removeRow = (id) => onChange(data.filter(row => row.id !== id));

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase w-1/4">Category</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase w-1/2">Training Name / Activity</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase w-1/4">Effort / Status</th>
                        {!isReadOnly && <th className="px-4 py-3 w-10"></th>}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((row) => (
                        <tr key={row.id}>
                            <td className="px-4 py-2 align-top"><input disabled={isReadOnly} className="w-full p-2 border rounded text-sm" value={row.category} onChange={(e) => updateRow(row.id, 'category', e.target.value)} /></td>
                            <td className="px-4 py-2 align-top"><textarea disabled={isReadOnly} className="w-full p-2 border rounded text-sm" rows="2" value={row.name} onChange={(e) => updateRow(row.id, 'name', e.target.value)} /></td>
                            <td className="px-4 py-2 align-top"><input disabled={isReadOnly} className="w-full p-2 border rounded text-sm" value={row.status} onChange={(e) => updateRow(row.id, 'status', e.target.value)} /></td>
                            {!isReadOnly && <td className="px-4 py-2 align-top text-center"><button onClick={() => removeRow(row.id)} className="text-red-400 hover:text-red-600"><TrashIcon className="h-5 w-5" /></button></td>}
                        </tr>
                    ))}
                </tbody>
            </table>
            {!isReadOnly && <button onClick={addRow} className="mt-2 text-sm text-[#20BEC6] font-medium hover:underline flex items-center"><PlusIcon className="h-4 w-4 mr-1" /> Add Row</button>}
        </div>
    );
}